import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect, useCallback } from 'react';
import MoodSelector, { applyMood } from './MoodSelector';
import SmartPromptBuilder from './SmartPromptBuilder';
import { detectLanguage, translatePrompt } from '../utils/promptTranslator';
import { VibeScoreMini } from './VibeScore';
import DraggablePromptChips from './DraggablePromptChips';

// ── Prompt Enhancer (client-side, no API) ─────────────────────
const QUALITY_MODIFIERS = [
  'cinematic lighting',
  'highly detailed',
  'award-winning digital art',
  'photorealistic textures',
  'dramatic composition',
  'professional illustration',
  'stunning visual impact',
  'rich color depth',
  'masterful shading',
  'gallery-worthy artwork',
];

const STYLE_MAP = [
  { keywords: ['cyberpunk', 'neon', 'cyber', 'holographic'], hints: ['neon glow effects', 'rain-soaked reflections', 'futuristic atmosphere'] },
  { keywords: ['vintage', 'retro', '70s', '80s', 'synthwave'], hints: ['aged film grain texture', 'warm nostalgic tones', 'hand-crafted aesthetics'] },
  { keywords: ['zen', 'minimal', 'minimalist', 'ink', 'japanese'], hints: ['negative space composition', 'ink wash technique', 'serene atmosphere'] },
  { keywords: ['galaxy', 'cosmic', 'space', 'star', 'nebula', 'celestial'], hints: ['luminous nebula colors', 'cosmic depth', 'ethereal stardust glow'] },
  { keywords: ['dark', 'gothic', 'shadow', 'skull', 'haunted'], hints: ['dramatic shadows', 'moody atmosphere', 'chiaroscuro lighting'] },
  { keywords: ['watercolor', 'paint', 'brush', 'artistic'], hints: ['flowing watercolor washes', 'expressive brushwork', 'rich pigment blending'] },
  { keywords: ['geometric', 'abstract', 'polygon', 'triangle'], hints: ['precise vector shapes', 'bold geometric contrast', 'clean architectural lines'] },
  { keywords: ['nature', 'forest', 'mountain', 'ocean', 'tree'], hints: ['organic textures', 'natural color palette', 'serene environmental depth'] },
];

const T_SHIRT_SUFFIX = 't-shirt print design, clean background, crisp edges';

function enhancePrompt(raw) {
  if (!raw.trim()) return raw;
  const prompt = raw.trim();
  const lp = prompt.toLowerCase();

  // Find matching style hints
  let styleHints = ['vibrant colors', 'dynamic composition', 'eye-catching design'];
  for (const entry of STYLE_MAP) {
    if (entry.keywords.some(kw => lp.includes(kw))) {
      styleHints = entry.hints;
      break;
    }
  }

  // Pick 2 random quality modifiers
  const shuffled = [...QUALITY_MODIFIERS].sort(() => Math.random() - 0.5);
  const qualityPicked = shuffled.slice(0, 2);

  // Compose
  const parts = [prompt, ...styleHints.slice(0, 2), ...qualityPicked, T_SHIRT_SUFFIX];
  return parts.join(', ');
}

const EXAMPLE_CHIPS = [
  { label: 'cyberpunk cat 🐱', prompt: 'cyberpunk cat with neon lights and holographic fur' },
  { label: 'galaxy dragon 🐉', prompt: 'galaxy dragon made of stars and cosmic energy' },
  { label: 'zen mountain 🏔️', prompt: 'minimalist zen mountain with cherry blossom and mist' },
  { label: 'neon octopus 🐙', prompt: 'neon octopus with glowing tentacles in deep ocean' },
  { label: 'vintage astronaut 🚀', prompt: 'vintage retro astronaut on the moon in 70s style' },
];

// Large pool of creative prompts for the "Surprise Me" button
const SURPRISE_PROMPTS = [
  'psychedelic fox in a neon jungle, vibrant colors, trippy art',
  'ancient samurai warrior with cherry blossom petals swirling around',
  'deep sea bioluminescent jellyfish, glowing blues and purples',
  'retro 80s synthwave wolf howling at a neon moon',
  'steampunk butterfly with clockwork wings and golden gears',
  'arctic wolf made of northern lights and aurora borealis',
  'urban street art tiger breaking through a brick wall',
  'celestial owl surrounded by moons planets and stardust',
  'origami crane dissolving into golden light particles',
  'glitchcore aesthetic portrait with chromatic aberration and vaporwave colors',
  'japanese koi fish transforming into a phoenix rising from water',
  'skull made of roses and wildflowers, gothic floral',
  'futuristic space whale swimming through a nebula',
  'abstract geometric bear built from triangles and gradient colors',
  'mushroom kingdom landscape with bioluminescent fungi at night',
  'ancient tree of life with roots as galaxies and branches as universes',
  'neon-lit ramen bowl with steam rising in kanji characters',
  'double-exposure wolf merging with a pine forest at sunset',
  'watercolor dragon made of ink splashes dissolving into clouds',
  'minimalist black cat silhouette with constellation stars inside',
  'graffiti-style lion roaring with paint splatter and urban textures',
  'holographic mermaid tail shimmering with iridescent scales',
  'retro cassette tape with city skyline inside the reels',
  'robot hand reaching for a blooming flower in a wasteland',
  'cosmic mandala with sacred geometry patterns, gold and violet',
  'vaporwave sunset beach with palm trees and pastel gradients',
  'phoenix rising from pixel art flames in 8-bit style',
  'lone samurai meditating under a waterfall surrounded by mist',
  'hand-drawn compass rose with hidden constellations inside',
  'haunted forest with glowing eyes and fireflies in fog',
];

const HISTORY_KEY = 'fashionai-prompt-history';
const MAX_HISTORY = 10;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToHistory(prompt) {
  try {
    const prev = loadHistory();
    // Remove duplicates, put new item first
    const deduped = prev.filter(p => p !== prompt);
    const next = [prompt, ...deduped].slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {}
}

// Detect Mac for kbd hint display
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

// ── Sentiment / mood detection ────────────────────────────────
const SENTIMENT_MOODS = [
  { id: 'dark',     emoji: '🌙', label: 'Dark',    keywords: ['dark', 'gothic', 'skull', 'shadow', 'death', 'horror', 'vampire', 'haunted', 'ghost', 'grim', 'noir', 'black', 'death', 'sinister', 'evil', 'cursed'] },
  { id: 'electric', emoji: '⚡', label: 'Electric', keywords: ['neon', 'electric', 'cyberpunk', 'cyber', 'holographic', 'glitch', 'laser', 'plasma', 'tech', 'digital', 'lightning', 'volt', 'holo', 'rave', 'futuristic'] },
  { id: 'nature',   emoji: '🌿', label: 'Nature',  keywords: ['forest', 'nature', 'tree', 'mountain', 'ocean', 'leaf', 'flower', 'botanical', 'jungle', 'meadow', 'river', 'garden', 'bloom', 'organic', 'earth', 'moss', 'fern', 'zen', 'bonsai'] },
  { id: 'intense',  emoji: '🔥', label: 'Intense',  keywords: ['fire', 'flame', 'fierce', 'dragon', 'explosion', 'roar', 'warrior', 'battle', 'rage', 'fury', 'intense', 'wild', 'fierce', 'bold', 'power', 'force', 'storm'] },
  { id: 'surreal',  emoji: '🌀', label: 'Surreal',  keywords: ['surreal', 'psychedelic', 'dream', 'trippy', 'abstract', 'cosmic', 'mystical', 'spiritual', 'fractal', 'kaleidoscope', 'vortex', 'dimension', 'universe', 'galaxy', 'celestial', 'magic', 'mystic', 'ethereal', 'otherworldly'] },
  { id: 'light',    emoji: '☀️', label: 'Light',   keywords: ['bright', 'cheerful', 'happy', 'pastel', 'cute', 'kawaii', 'sunshine', 'rainbow', 'colorful', 'playful', 'fun', 'soft', 'gentle', 'warm', 'golden', 'spring', 'summer', 'joy', 'sweet'] },
];

function detectSentiment(text) {
  if (!text || text.trim().length < 3) return null;
  const lower = text.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const mood of SENTIMENT_MOODS) {
    const score = mood.keywords.filter(kw => lower.includes(kw)).length;
    if (score > bestScore) { bestScore = score; best = mood; }
  }
  return bestScore > 0 ? best : null;
}

const PromptInput = forwardRef(function PromptInput({ onGenerate, isLoading }, ref) {
  const [prompt, setPrompt] = useState('');
  const [activeChip, setActiveChip] = useState(null);
  const [highlighted, setHighlighted] = useState(false);
  const [surprisePulse, setSurprisePulse] = useState(false);
  const [history, setHistory] = useState(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [activeMood, setActiveMood] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanceDone, setEnhanceDone] = useState(false);
  const [detectedLang, setDetectedLang] = useState(null); // { lang, name }
  const [translatedPrompt, setTranslatedPrompt] = useState(null); // string | null
  const [translationDismissed, setTranslationDismissed] = useState(false);
  const [sentiment, setSentiment] = useState(null); // { id, emoji, label }
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // ── Draggable chips state ────────────────────────────────
  // Split prompt into word-tokens for the chip strip (only show when ≥ 2 words)
  const promptWords = prompt.trim()
    ? prompt.trim().split(/\s+/).filter(Boolean)
    : [];
  const showDraggableChips = promptWords.length >= 2;

  const handleChipsChange = useCallback((newWords) => {
    const joined = newWords.join(' ');
    setPrompt(joined);
    setActiveChip(null);
    setTranslationDismissed(false);
    if (joined.trim().length >= 5) {
      const lang = detectLanguage(joined);
      if (lang) {
        setDetectedLang(lang);
        setTranslatedPrompt(translatePrompt(joined, lang.lang));
      } else {
        setDetectedLang(null);
        setTranslatedPrompt(null);
      }
      setSentiment(detectSentiment(joined));
    } else {
      setDetectedLang(null);
      setTranslatedPrompt(null);
      setSentiment(null);
    }
  }, []);

  // Expose setPrompt + focus to parent via ref
  useImperativeHandle(ref, () => ({
    setPrompt: (value) => {
      setPrompt(value);
      setActiveChip(null);
      setShowHistory(false);
      // Trigger highlight pulse and focus the input
      setHighlighted(true);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    },
  }));

  // Remove highlight after animation completes
  useEffect(() => {
    if (!highlighted) return;
    const t = setTimeout(() => setHighlighted(false), 1200);
    return () => clearTimeout(t);
  }, [highlighted]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showHistory) return;
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showHistory]);

  // Keyboard shortcuts: Cmd/Ctrl+Enter to generate, Escape to clear
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl+Enter — trigger generate from anywhere
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (prompt.trim() && !isLoading) {
          const trimmed = prompt.trim();
          saveToHistory(trimmed);
          setHistory(loadHistory());
          setShowHistory(false);
          const effectivePrompt = (detectedLang && translatedPrompt && !translationDismissed)
            ? translatedPrompt
            : trimmed;
          onGenerate(applyMood(effectivePrompt, activeMood));
        }
        return;
      }
      // Escape — clear input when focused
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        e.preventDefault();
        setPrompt('');
        setActiveChip(null);
        setShowHistory(false);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [prompt, isLoading, activeMood, onGenerate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!prompt.trim() || isLoading) return;
    const trimmed = prompt.trim();
    saveToHistory(trimmed);
    setHistory(loadHistory());
    setShowHistory(false);
    // If a translation is available and not dismissed, use it for generation
    const effectivePrompt = (detectedLang && translatedPrompt && !translationDismissed)
      ? translatedPrompt
      : trimmed;
    onGenerate(applyMood(effectivePrompt, activeMood));
  };

  const handleChipClick = (chip) => {
    if (isLoading) return;
    if (activeChip === chip.label) {
      setPrompt('');
      setActiveChip(null);
      setSentiment(null);
    } else {
      setPrompt(chip.prompt);
      setActiveChip(chip.label);
      setSentiment(detectSentiment(chip.prompt));
    }
    setShowHistory(false);
    setDetectedLang(null);
    setTranslatedPrompt(null);
    setTranslationDismissed(false);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setPrompt(val);
    if (activeChip) setActiveChip(null);
    setTranslationDismissed(false);
    // Run language detection once there are enough characters
    if (val.trim().length >= 5) {
      const lang = detectLanguage(val);
      if (lang) {
        setDetectedLang(lang);
        setTranslatedPrompt(translatePrompt(val, lang.lang));
      } else {
        setDetectedLang(null);
        setTranslatedPrompt(null);
      }
      setSentiment(detectSentiment(val));
    } else {
      setDetectedLang(null);
      setTranslatedPrompt(null);
      setSentiment(null);
    }
  };

  const handleInputFocus = () => {
    if (history.length > 0) setShowHistory(true);
  };

  const handleSelectHistory = (item) => { // eslint-disable-line no-unused-vars
    setPrompt(item);
    setActiveChip(null);
    setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleClearHistory = (e) => { // eslint-disable-line no-unused-vars
    e.stopPropagation();
    try { localStorage.removeItem(HISTORY_KEY); } catch {}
    setHistory([]);
    setShowHistory(false);
  };

  // Smart Prompt Builder callback — merges builder output into the input
  const handleBuilderChange = useCallback((built) => {
    if (!built) return; // ignore empty (user cleared)
    setPrompt(built);
    setActiveChip(null);
    setShowHistory(false);
  }, []);

  const handleEnhance = () => {
    if (isLoading || enhancing || !prompt.trim()) return;
    setEnhancing(true);
    setEnhanceDone(false);
    // Simulate a brief "thinking" delay for effect
    setTimeout(() => {
      const enhanced = enhancePrompt(prompt.trim());
      setPrompt(enhanced);
      setActiveChip(null);
      setShowHistory(false);
      setEnhancing(false);
      setEnhanceDone(true);
      // Flash ✅ for 1.5s then revert
      setTimeout(() => setEnhanceDone(false), 1500);
      inputRef.current?.focus();
    }, 600);
  };

  const handleSurpriseMe = () => {
    if (isLoading) return;
    let candidates = SURPRISE_PROMPTS.filter(p => p !== prompt);
    if (candidates.length === 0) candidates = SURPRISE_PROMPTS;
    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    setPrompt(picked);
    setActiveChip(null);
    setShowHistory(false);
    setDetectedLang(null);
    setTranslatedPrompt(null);
    setTranslationDismissed(false);
    setSurprisePulse(true);
    setTimeout(() => setSurprisePulse(false), 600);
    setSentiment(detectSentiment(picked));
    inputRef.current?.focus();
  };

  const MAX_CHARS = 200;
  const charCount = prompt.length;
  const charWarning = charCount >= 190 ? 'danger' : charCount >= 150 ? 'warn' : '';

  return (
    <section className="prompt-section">
      <form className="prompt-form" onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <button
          type="button"
          className={`surprise-btn${surprisePulse ? ' surprise-pulse' : ''}`}
          onClick={handleSurpriseMe}
          disabled={isLoading}
          title="Surprise me — pick a random creative prompt"
          aria-label="Surprise me with a random prompt"
        >
          🎲
        </button>
        <input
          ref={inputRef}
          className={`prompt-input${highlighted ? ' prompt-input--highlighted' : ''}`}
          type="text"
          placeholder='Describe your design... or hit 🎲 for a surprise!'
          value={prompt}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          autoComplete="off"
        />
        <button
          type="button"
          className={`enhance-btn${enhancing ? ' enhancing' : ''}${enhanceDone ? ' enhance-done' : ''}`}
          onClick={handleEnhance}
          disabled={isLoading || enhancing || !prompt.trim()}
          title="✨ Enhance — rewrite your prompt into a richer artistic description"
          aria-label="Enhance prompt"
        >
          {enhanceDone ? '✅' : enhancing ? (
            <span className="enhance-spinner" />
          ) : '✨ Enhance'}
        </button>
        <button
          className="prompt-submit"
          type="submit"
          disabled={isLoading || !prompt.trim() || charCount > MAX_CHARS}
          title={`Generate design (${isMac ? '⌘' : 'Ctrl'}+Enter)`}
        >
          {isLoading ? 'Generating...' : (
            <>
              ✨ Generate
              <span className="prompt-kbd-hint">
                <kbd>{isMac ? '⌘' : 'Ctrl'}</kbd><kbd>↵</kbd>
              </span>
            </>
          )}
        </button>
      </form>
      {/* Sentiment indicator badge + Vibe Score mini */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: sentiment || prompt.length > 2 ? '6px' : '0' }}>
        {sentiment && (
          <div className="prompt-sentiment-badge" key={sentiment.id} aria-label={`Mood: ${sentiment.label}`}>
            <span className="prompt-sentiment-emoji">{sentiment.emoji}</span>
            <span className="prompt-sentiment-label">{sentiment.label}</span>
          </div>
        )}
        {prompt.length > 2 && <VibeScoreMini prompt={prompt} />}
      </div>
      {charCount > 0 && (
        <div className={`prompt-char-counter${charWarning ? ` prompt-char-counter--${charWarning}` : ''}`}>
          {charCount}/{MAX_CHARS}
        </div>
      )}

      {/* Draggable prompt word chips */}
      {showDraggableChips && (
        <DraggablePromptChips
          words={promptWords}
          onChange={handleChipsChange}
          disabled={isLoading}
        />
      )}

      {/* Translation banner */}
      {detectedLang && translatedPrompt && !translationDismissed && (
        <div className="prompt-translation-banner">
          <span className="prompt-translation-icon">🌐</span>
          <span className="prompt-translation-text">
            We detected <strong>{detectedLang.name}</strong> — auto-translating for best results ✨
          </span>
          <span className="prompt-translation-pill" title="Will generate using this translation">
            {translatedPrompt}
          </span>
          <button
            type="button"
            className="prompt-translation-dismiss"
            onClick={() => setTranslationDismissed(true)}
            title="Dismiss — use original prompt"
            aria-label="Dismiss translation"
          >
            ✕
          </button>
        </div>
      )}
      <div className="prompt-chips">
        {EXAMPLE_CHIPS.map((chip) => (
          <button
            key={chip.label}
            type="button"
            className={`prompt-chip${activeChip === chip.label ? ' active' : ''}`}
            onClick={() => handleChipClick(chip)}
            disabled={isLoading}
            title={chip.prompt}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <MoodSelector
        activeMood={activeMood}
        onSelect={setActiveMood}
        disabled={isLoading}
      />
      <SmartPromptBuilder
        onPromptChange={handleBuilderChange}
        disabled={isLoading}
      />
    </section>
  );
});

export default PromptInput;
